/**
 * Recording Service
 *
 * Handles video recording using MediaRecorder API and uploads to Supabase Storage
 */

import { supabase } from "../supabaseClient";

export interface RecordingMetadata {
  id?: string;
  diagramId?: string;
  title?: string;
  description?: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  settings?: {
    aspectRatio: string;
    frameSize: { width: number; height: number };
    showCamera: boolean;
    cornerRadius: number;
  };
}

export interface RecordingResult {
  id: string;
  storage_path: string;
  video_url: string;
  recording: RecordingMetadata;
}

// Store chunks per recorder instance
const recorderChunks = new WeakMap<MediaRecorder, Blob[]>();

// Store cleanup callbacks for canvas-pipeline resources
const recorderCleanup = new WeakMap<MediaRecorder, () => void>();

/**
 * List available audio input devices
 */
export async function getAudioDevices(): Promise<MediaDeviceInfo[]> {
  try {
    // Request permission first so labels are available
    const tempStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    tempStream.getTracks().forEach((t) => t.stop());

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === "audioinput");
  } catch {
    return [];
  }
}

/**
 * Start recording for a specific frame area.
 *
 * Pipeline: DOM canvases → composite onto off-screen canvas → captureStream → MediaRecorder
 *
 * This reads directly from the Excalidraw <canvas> elements on the page,
 * completely bypassing getDisplayMedia().  Benefits:
 *   • No browser sharing dialog or confirmation prompt
 *   • No "sharing this tab" bar that compresses the viewport
 *   • Perfect pixel-accurate crop of the frame area
 *   • Works identically across Chrome, Edge, Firefox, Safari
 *
 * The avatar bubble webcam feed is composited as a circular overlay.
 *
 * @param frameRect  - CSS-pixel rect of the recording frame on the viewport
 * @param microphoneDeviceId - device ID for microphone, or "none" to disable audio
 */
export async function startScreenRecording(
  frameRect: { x: number; y: number; width: number; height: number },
  microphoneDeviceId: string = "none",
): Promise<MediaRecorder> {
  try {
    // ── 1. Request microphone ────────────────────────────────────────
    let micStream: MediaStream | null = null;

    if (microphoneDeviceId && microphoneDeviceId !== "none") {
      try {
        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 48000 },
          sampleSize: { ideal: 16 },
          channelCount: { ideal: 1 },
        };
        if (microphoneDeviceId !== "default") {
          audioConstraints.deviceId = { exact: microphoneDeviceId };
        }
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        });
      } catch (micError) {
        console.warn(
          "[Recording] Microphone access failed, continuing without audio:",
          micError,
        );
      }
    }

    // ── 2. Find source canvas layers on the page ─────────────────────
    // Excalidraw renders into <canvas> elements inside .excalidraw.
    // We composite all layers (static + interactive) in DOM order.
    const excalidrawEl = document.querySelector(".excalidraw");
    const sourceCanvases: HTMLCanvasElement[] = excalidrawEl
      ? Array.from(excalidrawEl.querySelectorAll("canvas"))
      : Array.from(document.querySelectorAll("canvas"));

    if (sourceCanvases.length === 0) {
      micStream?.getTracks().forEach((t) => t.stop());
      throw new Error("No canvas elements found on page");
    }

    // Pre-compute crop coordinates for each source canvas.
    // Maps from CSS viewport frame-rect → canvas pixel-buffer coords.
    const layerInfo = sourceCanvases.map((c) => {
      const rect = c.getBoundingClientRect();
      const dpr = c.width / rect.width;
      return {
        canvas: c,
        srcX: Math.round((frameRect.x - rect.left) * dpr),
        srcY: Math.round((frameRect.y - rect.top) * dpr),
        srcW: Math.round(frameRect.width * dpr),
        srcH: Math.round(frameRect.height * dpr),
      };
    });

    // ── 3. Calculate output resolution ─────────────────────────────
    // Prefer the native canvas pixel resolution for maximum sharpness
    // (on Retina displays this is 2× the CSS size, e.g. 2560×1440).
    // Floor at 1080p equivalent so the output is never too small.
    const primaryCanvas = sourceCanvases[0];
    const primaryRect = primaryCanvas.getBoundingClientRect();
    const srcDpr = primaryCanvas.width / primaryRect.width;

    const ratio = frameRect.width / frameRect.height;
    let outW = Math.round(frameRect.width * srcDpr);
    let outH = Math.round(frameRect.height * srcDpr);

    // Ensure at least 1080p on the shorter dimension
    if (ratio >= 1 && outH < 1080) {
      outH = 1080;
      outW = Math.round(1080 * ratio);
    } else if (ratio < 1 && outW < 1080) {
      outW = 1080;
      outH = Math.round(1080 / ratio);
    }
    if (outW % 2 !== 0) {
      outW += 1;
    }
    if (outH % 2 !== 0) {
      outH += 1;
    }

    // ── 4. Create output canvas for compositing ──────────────────────
    const outCanvas = document.createElement("canvas");
    outCanvas.width = outW;
    outCanvas.height = outH;
    const ctx = outCanvas.getContext("2d")!;

    // Scale factor: output pixels per CSS pixel of the frame
    const outScale = outW / frameRect.width;

    // ── 5. Render loop (throttled to 30 fps) ─────────────────────────
    let rafId = 0;
    let lastDrawTime = 0;
    const FRAME_INTERVAL = 1000 / 30;

    const draw = (timestamp: number) => {
      rafId = requestAnimationFrame(draw);
      if (timestamp - lastDrawTime < FRAME_INTERVAL) {
        return;
      }
      lastDrawTime = timestamp;

      // 5a. White background (fills any gap if frame extends beyond canvas)
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outW, outH);

      // 5b. Draw each source canvas layer
      for (const layer of layerInfo) {
        try {
          ctx.drawImage(
            layer.canvas,
            layer.srcX,
            layer.srcY,
            layer.srcW,
            layer.srcH,
            0,
            0,
            outW,
            outH,
          );
        } catch {
          // Canvas may be tainted by cross-origin images — skip layer
        }
      }

      // 5c. Composite the avatar bubble (circular webcam overlay)
      // The on-screen avatar uses CSS scaleX(-1) to mirror the webcam
      // (natural selfie view).  We replicate that flip here so the
      // exported video matches what the user sees on screen.
      const avatarEl = document.querySelector(
        ".avatar-bubble",
      ) as HTMLElement | null;
      if (avatarEl) {
        const avatarVideo = avatarEl.querySelector(
          "video",
        ) as HTMLVideoElement | null;
        if (avatarVideo && avatarVideo.readyState >= 2) {
          const aRect = avatarEl.getBoundingClientRect();
          const ax = (aRect.left - frameRect.x) * outScale;
          const ay = (aRect.top - frameRect.y) * outScale;
          const aSize = aRect.width * outScale;

          ctx.save();
          // Circular clip
          ctx.beginPath();
          ctx.arc(ax + aSize / 2, ay + aSize / 2, aSize / 2, 0, Math.PI * 2);
          ctx.clip();
          // Mirror horizontally (same as CSS scaleX(-1) on the video)
          ctx.translate(ax + aSize, ay);
          ctx.scale(-1, 1);
          ctx.drawImage(avatarVideo, 0, 0, aSize, aSize);
          ctx.restore();
        }
      }
    };
    rafId = requestAnimationFrame(draw);

    // ── 6. Capture output canvas stream at 30 fps ────────────────────
    const canvasStream = outCanvas.captureStream(30);

    // ── 7. Build combined stream (canvas video + mic audio) ──────────
    const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];
    if (micStream) {
      tracks.push(...micStream.getAudioTracks());
    }
    const combinedStream = new MediaStream(tracks);

    // ── 8. Create MediaRecorder ──────────────────────────────────────
    const mimeType = MediaRecorder.isTypeSupported("video/mp4")
      ? "video/mp4"
      : MediaRecorder.isTypeSupported("video/webm; codecs=vp9")
      ? "video/webm; codecs=vp9"
      : "video/webm";

    const recorder = new MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: 12_000_000, // 12 Mbps for native-res sharpness
      audioBitsPerSecond: 192_000,
    });

    // ── 9. Register cleanup ──────────────────────────────────────────
    recorderCleanup.set(recorder, () => {
      cancelAnimationFrame(rafId);
      micStream?.getTracks().forEach((t) => t.stop());
    });

    // ── 10. Collect chunks ───────────────────────────────────────────
    const chunks: Blob[] = [];
    recorderChunks.set(recorder, chunks);

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    return recorder;
  } catch (error) {
    console.error("Failed to start recording:", error);
    throw new Error("Failed to start recording. Please check permissions.");
  }
}

/**
 * Stop recording and get video blob.
 *
 * Handles both normal stop and the case where the recorder was already
 * stopped (e.g. user clicked the browser's "Stop sharing" bar).
 */
export function stopRecording(recorder: MediaRecorder): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const chunks = recorderChunks.get(recorder) || [];

    const runCleanup = () => {
      // Stop combined stream tracks (canvas video + mic audio)
      recorder.stream.getTracks().forEach((track) => track.stop());
      recorderChunks.delete(recorder);

      // Clean up render loop and mic stream
      const cleanup = recorderCleanup.get(recorder);
      if (cleanup) {
        cleanup();
        recorderCleanup.delete(recorder);
      }
    };

    // Already stopped (e.g. user called stop from UI while state was already inactive)
    if (recorder.state === "inactive") {
      const blob = new Blob(chunks, { type: recorder.mimeType });
      runCleanup();
      resolve(blob);
      return;
    }

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType });
      runCleanup();
      resolve(blob);
    };

    recorder.onerror = (event) => {
      console.error("Recording error:", event);
      runCleanup();
      reject(new Error("Recording failed"));
    };

    recorder.stop();
  });
}

import { getBackendUrl } from "../utils/backendUrl";

const AI_BACKEND_BASE = getBackendUrl();

/**
 * Convert recording to MP4+AAC via backend FFmpeg (no client WASM).
 * Use when needsConversion is true (WebM or Windows MP4+Opus).
 */
export async function convertToMp4ViaBackend(blob: Blob): Promise<Blob> {
  const ext = blob.type.includes("mp4") ? "mp4" : "webm";
  const formData = new FormData();
  formData.append("file", blob, `input.${ext}`);

  const response = await fetch(`${AI_BACKEND_BASE}/v1/ai/recordings/convert`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    let detail = "Conversion failed";
    try {
      const body = await response.json();
      if (body?.detail) {
        detail =
          typeof body.detail === "string" ? body.detail : String(body.detail);
      }
    } catch {
      const text = await response.text();
      if (text) {
        detail = text;
      }
    }
    throw new Error(detail);
  }

  return await response.blob();
}

/**
 * Upload recording to Supabase Storage and save metadata
 */
export async function uploadRecording(
  videoBlob: Blob,
  metadata: RecordingMetadata,
): Promise<RecordingResult> {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("User must be authenticated to upload recordings");
  }

  // Generate unique filename
  const recordingId = crypto.randomUUID();
  const fileName = `${recordingId}.webm`;
  const storagePath = `recordings/${user.id}/${fileName}`;

  try {
    // 1. Upload video to Supabase Storage
    const { data: _uploadData, error: uploadError } = await supabase.storage
      .from("recordings")
      .upload(storagePath, videoBlob, {
        contentType: videoBlob.type,
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }

    // 2. Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("recordings").getPublicUrl(storagePath);

    // 3. Save metadata to database
    const { data: recordingData, error: dbError } = await supabase
      .from("recordings")
      .insert({
        id: recordingId,
        user_id: user.id,
        diagram_id: metadata.diagramId,
        title: metadata.title || "Untitled Recording",
        description: metadata.description,
        storage_path: storagePath,
        video_url: publicUrl,
        duration_seconds: metadata.duration_seconds,
        file_size_bytes: videoBlob.size,
        mime_type: videoBlob.type,
        settings: metadata.settings,
        status: "completed",
      })
      .select()
      .single();

    if (dbError) {
      // Cleanup: delete uploaded file if database insert fails
      await supabase.storage.from("recordings").remove([storagePath]);
      throw dbError;
    }

    return {
      id: recordingId,
      storage_path: storagePath,
      video_url: publicUrl,
      recording: recordingData,
    };
  } catch (error) {
    console.error("Failed to upload recording:", error);
    throw error;
  }
}

/**
 * Delete recording
 */
export async function deleteRecording(recordingId: string): Promise<void> {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("User must be authenticated");
  }

  // 1. Get recording metadata
  const { data: recording, error: fetchError } = await supabase
    .from("recordings")
    .select("storage_path")
    .eq("id", recordingId)
    .eq("user_id", user.id)
    .single();

  if (fetchError || !recording) {
    throw new Error("Recording not found");
  }

  // 2. Delete from storage
  const { error: storageError } = await supabase.storage
    .from("recordings")
    .remove([recording.storage_path]);

  if (storageError) {
    console.error("Failed to delete video file:", storageError);
  }

  // 3. Delete from database
  const { error: dbError } = await supabase
    .from("recordings")
    .delete()
    .eq("id", recordingId)
    .eq("user_id", user.id);

  if (dbError) {
    throw dbError;
  }
}

/**
 * List user's recordings
 */
export async function listRecordings(diagramId?: string) {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("User must be authenticated");
  }

  let query = supabase
    .from("recordings")
    .select("id, diagram_id, title, description, video_url, thumbnail_url, duration_seconds, file_size_bytes, mime_type, status, created_at, updated_at")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false });

  if (diagramId) {
    query = query.eq("diagram_id", diagramId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Generate thumbnail from video blob
 */
export async function generateThumbnail(
  videoBlob: Blob,
  timeSeconds: number = 1,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    video.addEventListener("loadedmetadata", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    });

    video.addEventListener("seeked", () => {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to generate thumbnail"));
          }

          // Cleanup
          URL.revokeObjectURL(video.src);
        },
        "image/jpeg",
        0.8,
      );
    });

    video.addEventListener("error", () => {
      reject(new Error("Failed to load video"));
      URL.revokeObjectURL(video.src);
    });

    video.src = URL.createObjectURL(videoBlob);
    video.currentTime = timeSeconds;
  });
}
